import React, { useState } from "react";
import { useAddUserQuery } from "../../hooks/useUsers.ts";
import { FiSend } from "react-icons/fi";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { getTheme, ThemeName, ThemeColors } from '../../theme.ts';

const PostQuery: React.FC = () => {
  const { themeName } = useTheme();
  const currentTheme = getTheme(themeName);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState<boolean | null>(null);

  const addQueryMutation = useAddUserQuery();

  const reawdata = localStorage.getItem("user");
  let cred = JSON.parse(reawdata || "{}");
  let token = cred?.access;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    setSuccess(null);

    try {
      let id = cred.user?.id;
      const formdata = new FormData();
      formdata.append("user", id);
      formdata.append("description", description);

      await addQueryMutation.mutateAsync({ token, data: formdata }, {
        onSuccess: () => {
          setSuccess(true);
          setMessage("Your query has been submitted successfully!");
          setDescription("");
        },
        onError: () => {
          throw new Error("Failed");
        }
      });
    } catch (error) {
      setSuccess(false);
      setMessage("Failed to submit query. Please try again.");
    }

    setLoading(false);
  };

  return (
    <div
      className="flex justify-center items-center min-h-screen"
      style={{ backgroundColor: currentTheme.background }}
    >
      <div
        className="backdrop-blur-xl p-4 md:p-8 rounded-xl shadow-2xl w-full max-w-xl min-w-[15rem] md:min-w-[30rem] lg:min-w-[45rem] xl:min-w-[60rem] min-h-[15rem] md:min-h-[25rem] mt-[-2rem] transition-all duration-300"
        style={{
          backgroundColor: currentTheme.surface + 'E6', // 90% opacity
          borderColor: currentTheme.border,
          borderWidth: 1,
          borderStyle: 'solid'
        }}
      >
        {/* Decorative SVG */}
        <div className="flex justify-center mb-4">
          <svg
            className="w-16 h-16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            viewBox="0 0 24 24"
            style={{ color: currentTheme.primary }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 12.79A9 9 0 1111.21 3H12a9 9 0 019 9v.79z"
            />
          </svg>
        </div>

        <h2
          className="text-sm md:text-3xl font-extrabold text-center mb-2"
          style={{ color: currentTheme.text }}
        >
          Submit a Query
        </h2>
        <p
          className="text-center mb-6 text-sm"
          style={{ color: currentTheme.textSecondary }}
        >
          Have a question or concern? We'll get back to you quickly.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Textarea */}
          <div>
            <textarea
              className="w-full px-4 py-3 rounded-xl shadow-sm focus:outline-none resize-none transition-all"
              rows={5}
              placeholder="Type your query here..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              style={{
                backgroundColor: currentTheme.card,
                borderColor: currentTheme.border,
                borderWidth: 1,
                borderStyle: 'solid',
                color: currentTheme.text,
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
              }}
              onFocus={(e) => e.currentTarget.style.boxShadow = `0 0 0 3px ${currentTheme.primary}33`}
              onBlur={(e) => e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'}
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-5 px-10 rounded-xl font-semibold text-lg text-white transition-all duration-300 disabled:cursor-not-allowed"
            style={{
              backgroundColor: loading ? currentTheme.textMuted : currentTheme.primary,
              opacity: loading ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = currentTheme.hover || currentTheme.primary;
                e.currentTarget.style.transform = 'scale(0.98)';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = currentTheme.primary;
                e.currentTarget.style.transform = 'scale(1)';
              }
            }}
          >
            {loading ? "Submitting..." : "Submit Query"}
            {!loading && <FiSend className="text-lg" />}
          </button>
        </form>

        {/* Message Feedback */}
        {message && (
          <div
            className="mt-6 flex items-center gap-2 text-center justify-center text-sm font-medium"
            style={{
              color: success ? currentTheme.success : currentTheme.error
            }}
          >
            {success ? (
              <FaCheckCircle className="text-xl" />
            ) : (
              <FaTimesCircle className="text-xl" />
            )}
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

export default PostQuery;
